import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingShell } from "@/components/marketing-shell";
import { Button } from "@/components/ui/button";
import { redirectIfAuthenticated } from "@/lib/marketing-guards";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export const Route = createFileRoute("/faq")({
  beforeLoad: redirectIfAuthenticated,
  head: () => ({
    meta: [
      { title: "FAQ, Super Affiliate OS" },
      {
        name: "description",
        content:
          "Answers to common questions about Super Affiliate OS, pricing, trials, data security, supported networks, and team plans.",
      },
      { property: "og:title", content: "FAQ, Super Affiliate OS" },
      {
        property: "og:description",
        content: "Answers to common questions about Super Affiliate OS.",
      },
    ],
  }),
  component: FaqPage,
});

const faqs: { q: string; a: string }[] = [
  {
    q: "How does the 14-day free trial work?",
    a: "Pick any plan, drop in a card, and use the full product for 14 days. You won't be charged until day 15, and you can cancel from your billing settings at any time during the trial without paying a cent.",
  },
  {
    q: "Which affiliate networks do you support?",
    a: "We accept CSV exports from every major network and platform, ClickFunnels, OfferLab, ThriveCart, ShareASale, Impact, PartnerStack, CJ, and more. Our smart importer auto-detects columns, dedupes, and reconciles automatically.",
  },
  {
    q: "Can I change or cancel my plan later?",
    a: "Yes. Upgrade, downgrade, or cancel any time from Settings → Billing. Upgrades take effect immediately with prorated billing. Downgrades apply at your next renewal so you don't lose paid time.",
  },
  {
    q: "How is my data protected?",
    a: "Every row is protected by Postgres row-level security policies scoped to your account. We never share data across accounts, run regular security scans, and log every administrative action.",
  },
  {
    q: "Do you support teams?",
    a: "Pro and Super plans include teammate seats with role-based access (Owner, Admin, Member, Viewer). Invite via email, manage permissions in Settings → Team.",
  },
  {
    q: "What happens if I exceed my plan's workspace or import limit?",
    a: "We'll let you know in-app before you hit a hard wall. Upgrade in one click from your billing page, no data migration, no downtime.",
  },
  {
    q: "Do you offer custom or enterprise plans?",
    a: "Yes. For larger teams, custom contracts, or SSO requirements, reach out via the contact page and we'll put together a plan that fits.",
  },
  {
    q: "Can I export my data if I leave?",
    a: "Always. Every workspace can export commissions, contacts, and imports as CSV. Your data is yours.",
  },
];

function FaqPage() {
  return (
    <MarketingShell>
      <section className="mx-auto max-w-3xl px-6 py-20">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
            Frequently asked questions
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
            Can't find what you're looking for? <Link to="/contact" className="text-primary hover:underline">Get in touch</Link>.
          </p>
        </div>

        <Accordion type="single" collapsible className="mt-12">
          {faqs.map((item, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left text-base font-medium">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-16 rounded-2xl border border-border bg-card p-8 text-center">
          <h2 className="text-xl font-semibold">Still have questions?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            We reply to every message within one business day.
          </p>
          <Button asChild className="mt-6">
            <Link to="/contact">Contact us</Link>
          </Button>
        </div>
      </section>
    </MarketingShell>
  );
}
